import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppTooltip from '../AppTooltip/AppTooltip';
import Pagination from '../Pagination';
import SubmissionService, {
  GetInvalidDicomFilesParams,
  InvalidDicomFile,
} from '../../services/SubmissionService';
import loadingGif from '../../../assets/loading.gif';
import './InvalidDicomFilesModal.css';

const PAGE_SIZE = 5;
const TOOLTIP_MIN_LENGTH = 48;

const InvalidFilesClampedCell: React.FC<{
  text: string;
  lineClampClass?: string;
}> = ({ text, lineClampClass = 'invalid-dicom-files-cell-clamp--3' }) => {
  const display = text?.trim() || '—';
  const showTooltip = display !== '—' && display.length >= TOOLTIP_MIN_LENGTH;

  return (
    <AppTooltip
      side="top"
      sideOffset={6}
      maxWidth={400}
      disabled={!showTooltip}
      content={display}
      className="invalid-dicom-files-cell-tooltip"
      contentClassName="invalid-dicom-files-tooltip-content"
    >
      <span className={`invalid-dicom-files-cell-clamp ${lineClampClass}`}>{display}</span>
    </AppTooltip>
  );
};

export type InvalidDicomFilesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  submissionId: string;
  submissionTitle: string;
};

const InvalidDicomFilesModal: React.FC<InvalidDicomFilesModalProps> = ({
  isOpen,
  onClose,
  submissionId,
  submissionTitle,
}) => {
  const submissionServiceRef = useRef(SubmissionService.getInstance());
  const hasLoadedOnceRef = useRef(false);
  const [reserveLayoutSpace, setReserveLayoutSpace] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState<InvalidDicomFile[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const fetchInvalidFiles = useCallback(
    async (targetSubmissionId: string, targetPage: number, search?: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const requestParams: GetInvalidDicomFilesParams = {
          submission_id: targetSubmissionId,
          is_active: true,
          page: targetPage,
          size: PAGE_SIZE,
        };

        if (search?.trim()) {
          requestParams.search = search.trim();
        }

        const response = await submissionServiceRef.current.getInvalidDicomFiles(requestParams);

        if (response.success && response.data) {
          const nextTotalCount = response.data.total_count || 0;
          setInvalidFiles(response.data.invalidFiles || []);
          setTotalCount(nextTotalCount);
          setTotalPages(response.data.total_pages || 0);
          setPage(response.data.page || targetPage);
          if (nextTotalCount > 0) {
            setReserveLayoutSpace(true);
          }
        } else {
          setInvalidFiles([]);
          setTotalCount(0);
          setTotalPages(0);
          setError(response.error || 'Failed to load invalid files');
        }
      } catch (err) {
        console.error('Error fetching invalid files:', err);
        setInvalidFiles([]);
        setTotalCount(0);
        setTotalPages(0);
        setError('An unexpected error occurred while loading invalid files');
      } finally {
        hasLoadedOnceRef.current = true;
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!isOpen || !submissionId) {
      return;
    }

    hasLoadedOnceRef.current = false;
    setReserveLayoutSpace(false);
    setInvalidFiles([]);
    setPage(1);
    setTotalPages(0);
    setTotalCount(0);
    setError(null);
    setSearchQuery('');
    setAppliedSearch('');
    setIsLoading(true);
    fetchInvalidFiles(submissionId, 1);
  }, [isOpen, submissionId, fetchInvalidFiles]);

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    onClose();
  };

  const hasSearchInput = searchQuery.trim().length > 0;
  const hasActiveSearch = appliedSearch.length > 0 || hasSearchInput;
  const isRefreshing = isLoading && hasLoadedOnceRef.current;

  const handleSearch = () => {
    if (!submissionId) {
      return;
    }
    const trimmedSearch = searchQuery.trim();
    if (!trimmedSearch) {
      return;
    }
    setAppliedSearch(trimmedSearch);
    setPage(1);
    fetchInvalidFiles(submissionId, 1, trimmedSearch);
  };

  const handleClear = () => {
    if (!submissionId || isLoading) {
      return;
    }
    setSearchQuery('');
    setAppliedSearch('');
    setPage(1);
    fetchInvalidFiles(submissionId, 1);
  };

  const handlePageChange = (nextPage: number) => {
    if (!submissionId || nextPage === page) {
      return;
    }
    setPage(nextPage);
    fetchInvalidFiles(submissionId, nextPage, appliedSearch);
  };

  if (!isOpen || !submissionId) {
    return null;
  }

  return (
    <div className="invalid-dicom-files-modal-overlay">
      <div
        className="invalid-dicom-files-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="invalid-dicom-files-modal-title"
      >
        <div className="invalid-dicom-files-modal-header">
          <div className="invalid-dicom-files-modal-header-title">
            <h3 id="invalid-dicom-files-modal-title">Invalid DICOM Files</h3>
            <span className="invalid-dicom-files-submission-badge" title={submissionTitle}>
              {submissionTitle}
            </span>
          </div>
          <button
            type="button"
            className="invalid-dicom-files-modal-close"
            onClick={handleClose}
            disabled={isLoading}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="invalid-dicom-files-search-toolbar">
          <div className="invalid-dicom-files-search-container">
            <label htmlFor="invalid-dicom-files-search-input">File Name / Path</label>
            <div className="invalid-dicom-files-search-input-wrap">
              <input
                id="invalid-dicom-files-search-input"
                type="text"
                className="invalid-dicom-files-search-input"
                placeholder="Search file name or path..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && hasSearchInput) {
                    handleSearch();
                  }
                }}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="invalid-dicom-files-search-actions">
            <button
              type="button"
              className="invalid-dicom-files-search-button"
              onClick={handleSearch}
              title="Search invalid files"
              disabled={isLoading || !hasSearchInput}
            >
              Search
            </button>
            <button
              type="button"
              className="invalid-dicom-files-clear-button"
              onClick={handleClear}
              disabled={isLoading || !hasActiveSearch}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="invalid-dicom-files-modal-body" aria-busy={isLoading}>
          {error ? (
            <div className="invalid-dicom-files-error">{error}</div>
          ) : (
            <div className="invalid-dicom-files-table-wrapper">
              {isRefreshing && (
                <div className="invalid-dicom-files-table-loading" aria-live="polite">
                  <img
                    src={loadingGif}
                    alt=""
                    className="invalid-dicom-files-table-loading-gif"
                    aria-hidden="true"
                  />
                </div>
              )}
              <table
                className={`invalid-dicom-files-table${isRefreshing ? ' invalid-dicom-files-table--refreshing' : ''}`}
              >
                <thead>
                  <tr>
                    <th>Sl No</th>
                    <th>File Name</th>
                    <th>Reason</th>
                    <th>Path</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && !hasLoadedOnceRef.current ? (
                    <tr>
                      <td colSpan={4} className="invalid-dicom-files-loading-cell">
                        <img
                          src={loadingGif}
                          alt=""
                          className="invalid-dicom-files-loading-gif"
                          aria-hidden="true"
                        />
                        <p className="invalid-dicom-files-loading-text">Loading invalid files...</p>
                      </td>
                    </tr>
                  ) : !isLoading && invalidFiles.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="invalid-dicom-files-empty">
                        No Data Found
                      </td>
                    </tr>
                  ) : (
                    invalidFiles.map((file, index) => (
                      <tr key={file.id ?? `${file.fileName}-${index}`}>
                        <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                        <td className="invalid-dicom-files-filename-cell">
                          <InvalidFilesClampedCell
                            text={file.fileName}
                            lineClampClass="invalid-dicom-files-cell-clamp--2"
                          />
                        </td>
                        <td className="invalid-dicom-files-error-cell">
                          <InvalidFilesClampedCell
                            text={file.errorMessage || file.reason || ''}
                          />
                        </td>
                        <td className="invalid-dicom-files-path-cell">
                          <InvalidFilesClampedCell
                            text={file.quarantinePath || ''}
                            lineClampClass="invalid-dicom-files-cell-clamp--2"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!error && reserveLayoutSpace && (
          <div className="invalid-dicom-files-pagination-footer">
            {totalCount > 0 ? (
              <Pagination
                currentPage={page}
                totalPages={Math.max(totalPages, 1)}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageChange={handlePageChange}
                className="invalid-dicom-files-pagination"
              />
            ) : (
              <div className="pagination-container invalid-dicom-files-pagination">
                <div className="pagination-info">Showing 0 to 0 of 0 entries</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvalidDicomFilesModal;
